import React from 'react'
import { PremiumBreakdown } from '../types'

interface Props {
  breakdown: PremiumBreakdown
  hasTopUp: boolean
  hasFlexi: boolean
}

const formatCurrency = (amount: number): string => '₹' + amount.toLocaleString('en-IN')

const PremiumSummary: React.FC<Props> = ({ breakdown, hasTopUp, hasFlexi }) => {
  const {
    basePremium,
    baseGst,
    baseTotal,
    topupPremium,
    topupGst,
    topupTotal,
    flexiPremium,
    flexiGst,
    flexiTotal,
    totalGst,
    totalPremiumPayable,
    proRataFactor,
    walletAmount,
    finalPayable,
  } = breakdown

  if (totalPremiumPayable === 0 && basePremium === 0) return null

  return (
    <>
      <div className='separator separator-dashed my-3'></div>

      {/* Base Premium */}
      <div className='d-flex justify-content-between mb-2'>
        <span className='text-muted fs-7'>Base Premium</span>
        <span className='fw-bold fs-7'>{formatCurrency(basePremium)}</span>
      </div>
      <div className='d-flex justify-content-between mb-2'>
        <span className='text-muted fs-8 ms-3'>GST</span>
        <span className='fs-8'>{formatCurrency(baseGst)}</span>
      </div>
      <div className='d-flex justify-content-between mb-3'>
        <span className='fw-bold fs-7 ms-3'>Base Total</span>
        <span className='fw-bold fs-7'>{formatCurrency(baseTotal)}</span>
      </div>

      {/* Top-Up */}
      {hasTopUp && topupPremium > 0 && (
        <>
          <div className='separator separator-dashed my-3'></div>
          <div className='d-flex justify-content-between mb-2'>
            <span className='text-muted fs-7'>Top-Up Premium</span>
            <span className='fw-bold fs-7'>{formatCurrency(topupPremium)}</span>
          </div>
          <div className='d-flex justify-content-between mb-2'>
            <span className='text-muted fs-8 ms-3'>GST</span>
            <span className='fs-8'>{formatCurrency(topupGst)}</span>
          </div>
          <div className='d-flex justify-content-between mb-3'>
            <span className='fw-bold fs-7 ms-3'>Top-Up Total</span>
            <span className='fw-bold fs-7'>{formatCurrency(topupTotal)}</span>
          </div>
        </>
      )}

      {/* Flexi */}
      {hasFlexi && flexiPremium > 0 && (
        <>
          <div className='separator separator-dashed my-3'></div>
          <div className='d-flex justify-content-between mb-2'>
            <span className='text-muted fs-7'>Flexi Premium</span>
            <span className='fw-bold fs-7'>{formatCurrency(flexiPremium)}</span>
          </div>
          <div className='d-flex justify-content-between mb-2'>
            <span className='text-muted fs-8 ms-3'>GST</span>
            <span className='fs-8'>{formatCurrency(flexiGst)}</span>
          </div>
          <div className='d-flex justify-content-between mb-3'>
            <span className='fw-bold fs-7 ms-3'>Flexi Total</span>
            <span className='fw-bold fs-7'>{formatCurrency(flexiTotal)}</span>
          </div>
        </>
      )}

      {/* Grand Total */}
      <div className='separator my-3'></div>
      <div className='d-flex justify-content-between mb-1'>
        <span className='text-muted fs-7'>Total GST</span>
        <span className='fs-7'>{formatCurrency(totalGst)}</span>
      </div>
      <div className='d-flex justify-content-between mb-1'>
        <span className='fw-bold fs-7'>Total Payable</span>
        <span className='fw-bold fs-7'>{formatCurrency(totalPremiumPayable)}</span>
      </div>

      {/* Wallet Deduction */}
      {walletAmount > 0 && (
        <>
          <div className='d-flex justify-content-between mb-1'>
            <span className='text-muted fs-8'>
              <i className='bi bi-wallet2 me-1'></i>
              Wallet Deduction
            </span>
            <span className='text-success fs-8'>- {formatCurrency(walletAmount)}</span>
          </div>
          <div className='separator my-2'></div>
          <div className='d-flex justify-content-between'>
            <span className='fw-bolder fs-6'>Final Payable</span>
            <span className='fw-bolder fs-6 text-primary'>{formatCurrency(finalPayable)}</span>
          </div>
        </>
      )}

      {/* Pro-rata note */}
      {proRataFactor < 1 && (
        <div className='text-muted fs-8 mt-3'>
          <i className='bi bi-info-circle me-1'></i>
          Pro-rata applied ({Math.round(proRataFactor * 100)}% of policy period)
        </div>
      )}
    </>
  )
}

export default PremiumSummary
